import type { SupabaseClient, User } from '@supabase/supabase-js';
import { getProfession, professionToSpecialty } from '@/lib/professions';

const VALID_USER_TYPES = new Set(['professional', 'client']);

function profileFieldsFromUser(user: User) {
  const meta = user.user_metadata || {};
  const rawType = String(meta.user_type || meta.role || 'professional').toLowerCase();
  const userType = VALID_USER_TYPES.has(rawType) ? rawType : 'professional';
  const rawProfession = String(meta.profession || (userType === 'client' ? 'client' : 'lash'));
  const profession =
    getProfession(rawProfession)?.id || (userType === 'client' ? 'client' : 'lash');
  const fullName =
    meta.full_name || meta.name || (user.email ? user.email.split('@')[0] : '') || 'User';
  return { userType, profession, fullName };
}

/**
 * Ensures public.user_profiles (and artist_profiles for professionals) exist.
 * Listings and FKs (business_availability, etc.) require these rows, not just auth.users.
 */
export async function ensureUserProfile(
  supabase: SupabaseClient,
  user: User
): Promise<{ id: string } | null> {
  if (!user.email) return null;

  const seeded = profileFieldsFromUser(user);

  // 1. Check if profile already exists (public/authenticated read)
  const { data: existing } = await supabase
    .from('user_profiles')
    .select('id, user_type, profession, full_name, display_name, bio, location, country')
    .eq('id', user.id)
    .maybeSingle();

  let profile = existing;

  if (!profile) {
    // Check if client has a valid session matching user.id
    // If not authenticated in current client instance, writing directly triggers RLS violation
    const { data: sessionData } = await supabase.auth.getSession();
    const sessionUserId = sessionData?.session?.user?.id;

    if (sessionUserId === user.id) {
      const { data: created, error } = await supabase
        .from('user_profiles')
        .upsert(
          {
            id: user.id,
            email: user.email,
            full_name: seeded.fullName,
            user_type: seeded.userType,
            profession: seeded.profession,
          },
          { onConflict: 'id' }
        )
        .select('id, user_type, profession, full_name, display_name, bio, location, country')
        .maybeSingle();

      if (!error && created) {
        profile = created;
      }
    }

    // If client insert failed or was not possible (e.g. session token in flight or RLS restriction),
    // delegate to the server-side ensure route which can securely insert the row
    if (!profile && typeof window !== 'undefined') {
      try {
        const res = await fetch('/api/profile/ensure', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          const resData = await res.json();
          if (resData.profile) {
            profile = resData.profile;
          }
        }
      } catch {
        // Non-blocking fallback
      }
    }

    // Final check to see if handle_new_user database trigger or server created the profile
    if (!profile) {
      const { data: retryProfile } = await supabase
        .from('user_profiles')
        .select('id, user_type, profession, full_name, display_name, bio, location, country')
        .eq('id', user.id)
        .maybeSingle();
      profile = retryProfile;
    }
  }

  if (!profile) return null;

  if (profile.user_type === 'professional') {
    await ensureArtistProfile(supabase, user, profile);
  }

  return { id: profile.id };
}

async function ensureArtistProfile(
  supabase: SupabaseClient,
  user: User,
  profile: {
    user_type?: string | null;
    profession?: string | null;
    full_name?: string | null;
    display_name?: string | null;
    bio?: string | null;
    location?: string | null;
    country?: string | null;
  }
) {
  const { data: existing } = await supabase
    .from('artist_profiles')
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (existing?.id) return;

  const displayName =
    profile.display_name || profile.full_name || profileFieldsFromUser(user).fullName;
  const specialty = professionToSpecialty(profile.profession);

  // Check if client session matches before inserting into artist_profiles
  const { data: sessionData } = await supabase.auth.getSession();
  if (sessionData?.session?.user?.id !== user.id) {
    return;
  }

  const { error } = await supabase.from('artist_profiles').insert({
    user_id: user.id,
    display_name: displayName,
    specialty,
    bio: profile.bio || null,
    location: profile.location || null,
    country: profile.country || null,
    is_accepting_clients: true,
  });

  if (error && !/duplicate|unique|row-level security|policy/i.test(error.message)) {
    console.error('[ensureArtistProfile]', error.message);
  }
}
